// Kept in lockstep with pi_ecommerce_api's context.service.ts#resolveWorkflowContext
// (which fields actually get populated) and registry.ts/actions/* (which
// actions exist and what their config accepts). This is the single source
// both the condition/action pickers and their help modals read from, so the
// two never drift apart.

export const WORKFLOW_FIELD_REFERENCE = [
	{
		group: 'Order',
		fields: [
			{ path: 'order.id', description: 'Order ID.' },
			{ path: 'order.order_number', description: 'Customer-facing order number.' },
			{ path: 'order.source_order_id', description: 'Order ID from the storefront/source system.' },
			{ path: 'order.status', description: 'Financial status of the order.' },
			{ path: 'order.total', description: 'Order total (numeric).' },
			{ path: 'order.subtotal', description: 'Order subtotal before tax/shipping (numeric).' },
			{ path: 'order.payment_type', description: 'Payment type used on the order.' },
			{ path: 'order.po_number', description: 'Purchase order number, if provided.' },
			{ path: 'order.customer_id', description: 'ID of the customer who placed the order.' },
			{ path: 'order.order_date', description: 'Date/time the order was placed.' },
			{
				path: 'order.order_age_hours',
				description: 'Hours since the order was placed (numeric) -- use with > or >= to identify stalled/old orders, e.g. order.order_age_hours > 72.',
			},
			{
				path: 'order.shipped',
				description:
					"Whether the order has shipped (true/false). This system doesn't track delivery beyond shipment, so this is the practical stand-in for \"delivered\" -- refreshed by the Check Order Status action.",
			},
			{ path: 'order.fulfillment_status', description: 'Latest fulfillment status, if any -- refreshed by the Check Order Status action.' },
		],
	},
	{
		group: 'Customer',
		fields: [
			{ path: 'customer.id', description: 'Customer ID.' },
			{ path: 'customer.email', description: 'Customer email address.' },
			{ path: 'customer.first_name', description: 'Customer first name.' },
			{ path: 'customer.last_name', description: 'Customer last name.' },
			{ path: 'customer.company', description: 'Customer company name, if provided.' },
		],
	},
	{
		group: 'Product',
		fields: [
			{ path: 'product.id', description: 'Product ID.' },
			{ path: 'product.name', description: 'Product name.' },
			{ path: 'product.product_number', description: 'Product/SKU number.' },
		],
	},
	{
		group: 'Triggering event',
		fields: [
			{
				path: 'event.<field_name>',
				description:
					"The raw payload of the event that triggered this workflow -- varies by event type (e.g. inventory.updated's event.quantity_available, payment.failed's event.error_message). Check the event's payload shape for exact field names.",
			},
		],
	},
];

// Order/customer/product fields are only populated when the workflow's own
// entity is that type (or, for customer, when the order has one) -- e.g. a
// workflow triggered by inventory.updated won't have order.* or customer.*
// available, only product.* and event.*.
export const WORKFLOW_FIELD_AVAILABILITY_NOTE =
	'Only fields relevant to the entity this workflow runs against are populated. A workflow triggered by an order event has order.* (and customer.* if the order has one); one triggered by an inventory event has product.* instead. event.* is always available and reflects the specific event that fired.';

export const WORKFLOW_OPERATORS = [
	{ op: '=', description: 'Equal to' },
	{ op: '!=', description: 'Not equal to' },
	{ op: '>', description: 'Greater than (numeric)' },
	{ op: '>=', description: 'Greater than or equal to (numeric)' },
	{ op: '<', description: 'Less than (numeric)' },
	{ op: '<=', description: 'Less than or equal to (numeric)' },
	{ op: 'contains', description: 'Text contains the value, or the value is in an array field' },
	{ op: 'not_contains', description: 'Text does not contain the value, or the value is not in an array field' },
	{ op: 'in', description: 'Field matches one of a comma-separated list of values' },
	{ op: 'not_in', description: 'Field matches none of a comma-separated list of values' },
	{ op: 'is_null', description: 'Field has no value (value box is ignored)' },
	{ op: 'is_not_null', description: 'Field has a value (value box is ignored)' },
];

export const WORKFLOW_ACTION_REFERENCE = [
	{ type: 'hold_order', label: 'Hold order', description: 'Puts the order on hold. No configuration needed.' },
	{
		type: 'approve_order',
		label: 'Approve order',
		description: 'Marks the order ready for ERP submission and records an "order approved" event. No configuration needed.',
	},
	{
		type: 'reject_order',
		label: 'Reject order',
		description: 'Cancels the order and records an "order rejected" event.',
		config: [{ key: 'reason', description: 'Reason recorded on the order (optional -- defaults to "Rejected by workflow engine.").' }],
	},
	{
		type: 'send_email',
		label: 'Send email',
		description: 'Sends a one-off email.',
		config: [
			{ key: 'to', description: 'A field path (e.g. customer.email) or a literal email address.' },
			{ key: 'subject', description: 'Email subject. Supports {{field.path}} template variables.' },
			{ key: 'message', description: 'Email body. Supports {{field.path}} template variables.' },
		],
	},
	{
		type: 'notify_role',
		label: 'Notify a role',
		description: 'Emails every user matching the chosen recipient mode.',
		config: [
			{ key: 'recipientMode', description: 'ROLE_ADMIN (store admins), APPROVER_FLAG (designated order approvers), or EMAIL_LIST (specific addresses).' },
			{ key: 'recipientEmails', description: 'Comma-separated addresses -- only used when recipientMode is EMAIL_LIST.' },
			{ key: 'subject', description: 'Email subject. Supports {{field.path}} template variables.' },
			{ key: 'message', description: 'Email body. Supports {{field.path}} template variables.' },
		],
	},
	{
		type: 'send_to_erp',
		label: 'Send to ERP',
		description: 'Submits the order to the ERP (Pace). No configuration needed; safe to run more than once for the same step -- it will not create a duplicate submission.',
	},
	{
		type: 'check_order_status',
		label: 'Check order status',
		description:
			'Re-checks the order\'s current shipped/fulfillment status and refreshes order.shipped / order.fulfillment_status so a Branch step right after this one (e.g. after a Wait) sees live data instead of the value from when the workflow started. No configuration needed.',
	},
	{
		type: 'call_webhook',
		label: 'Call a webhook',
		description: 'POSTs the workflow context as JSON to an external URL.',
		config: [
			{ key: 'url', description: 'Destination URL.' },
			{ key: 'secret', description: 'Optional signing secret -- if set, the request is signed the same way subscribed webhooks are (X-PI-Webhook-Signature header).' },
		],
	},
];
